import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus, Search, Archive, FileSpreadsheet } from "lucide-react";
import { BulkVehicleUploadModal } from "../shared/BulkVehicleUploadModal";
import { api } from "../../lib/api";
import type { Tenant, Vehicle } from "../../lib/types";
import { daysLabel, CATEGORY_LABEL } from "../../lib/utils";
import { FilterChip } from "../core/Layout";
import { DataTable } from "../core/DataTable";
import { Button } from "../core/Button";
import { Input } from "../core/Field";
import { BucketBadge, Plate, StatusBadge } from "../core/Badges";
import { VehicleForm } from "../shared/VehicleForm";
import { CategoryPicker } from "../shared/CategoryPicker";

export function ClientVehiclesTab({ tenant, autoOpen, onOpened, onVehicle }: { tenant: Tenant; autoOpen: boolean; onOpened: () => void; onVehicle: (id: string) => void }) {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [archived, setArchived] = useState(false);
  const [open, setOpen] = useState(false);
  const [bulk, setBulk] = useState(false);
  useEffect(() => {
    if (autoOpen) { setOpen(true); onOpened(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpen]);
  const { data, isLoading } = useQuery<{ items: Vehicle[]; total: number }>({
    queryKey: ["vehicles", tenant.id, category, "", "", q, archived],
    queryFn: async () => (await api.get("/vehicles", { params: { tenant_id: tenant.id, category: category || undefined, q: q || undefined, include_deleted: archived ? "only" : "false", limit: 200 } })).data,
  });
  return (
    <div data-testid="client-vehicles-tab">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative"><Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" /><Input data-testid="client-vehicle-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Plate, make, model…" className="w-56 pl-8" /></div>
          <CategoryPicker compact allowAll value={category} onChange={setCategory} testIdPrefix="client-filter-category" />
          <FilterChip active={archived} onClick={() => setArchived(!archived)} testId="client-filter-archived"><span className="inline-flex items-center gap-1"><Archive className="h-3 w-3" />Archived</span></FilterChip>
        </div>
        <Button variant="secondary" onClick={() => setBulk(true)} data-testid="client-tab-bulk-upload-button"><FileSpreadsheet className="h-4 w-4" />Bulk upload</Button>
        <Button onClick={() => setOpen(true)} data-testid="client-tab-register-vehicle-button"><Plus className="h-4 w-4" />Register vehicle</Button>
      </div>
      <DataTable<Vehicle>
        testId="client-vehicles-table" loading={isLoading} rows={data?.items || []} rowKey={(v) => v.id} rowTestId={(v) => `client-vehicle-row-${v.id}`} onRowClick={(v) => onVehicle(v.id)}
        empty={archived ? "No archived vehicles." : "No vehicles registered yet. Register the first vehicle for this client."}
        columns={[
          { key: "plate", header: "Plate", render: (v) => <Plate plate={v.plate_number} /> },
          { key: "vehicle", header: "Vehicle", render: (v) => <div><p className="font-semibold text-slate-900">{v.make} {v.model}</p><p className="text-[11px] text-slate-500">{CATEGORY_LABEL[v.category]} · {v.sub_category}{v.year ? ` · ${v.year}` : ""}</p></div> },
          { key: "dept", header: "Department", render: (v) => <span className="text-xs">{v.department_name || <span className="text-slate-400">Unassigned</span>}</span> },
          { key: "status", header: "Status", render: (v) => <StatusBadge status={v.is_deleted ? "archived" : v.status} /> },
          { key: "compliance", header: "Compliance", render: (v) => <BucketBadge bucket={v.compliance_status} /> },
          { key: "next", header: "Next expiry", render: (v) => <span className="text-xs font-semibold text-slate-600">{daysLabel(v.next_expiry_days)}</span> },
          { key: "geotab", header: "Telematics", render: (v) => v.geotab_device_id ? <span className="font-mono text-[11px] text-slate-700">{v.geotab_device_id}</span> : <span className="text-[11px] text-slate-400">Not linked</span> },
        ]}
      />
      <VehicleForm open={open} onClose={() => setOpen(false)} lockedTenantId={tenant.id} />
      <BulkVehicleUploadModal open={bulk} onClose={() => setBulk(false)} lockedTenantId={tenant.id} />
    </div>
  );
}
